import { ArbreInteractif } from './arbre-navigable.js';
import type { DonneesArbre, Personne, StatutContact } from './types.js';

export type { StatutContact } from './types.js';

const partage = (c: Personne['contacts'][number]): boolean => c.visibility === 'famille' && !c.maskedForFamily;

/**
 * Statut de contact d'une personne vu par la famille (#9) : « accepte » si elle partage au moins une coordonnée,
 * « non accepté » si elle en a mais n'en partage aucune, « inconnu » si le Cabinet n'a encore rien.
 */
export function statutContact(p: Personne): StatutContact {
  if (p.vitalStatus === 'decede') return 'defunt';
  if (p.contacts.some(partage)) return 'accepte';
  return p.contacts.length > 0 ? 'non_accepte' : 'inconnu';
}

/** Statuts de toute la famille, calculés sur les données de l'équipe avant projection. */
export const statutsContact = (d: DonneesArbre): Record<string, StatutContact> =>
  Object.fromEntries(d.personnes.map((p) => [p.id, statutContact(p)]));

/**
 * Personne que la famille ne voit que par son lien de parenté : le mandant (jamais nommé aux autres héritiers) et toute
 * personne qui porte une mention sensible non partagée hors équipe (SYNTHESE §3, correction 3). Celui qui consulte
 * reste nommé pour lui-même.
 */
export const estAnonymeEnFamille = (p: Personne, viewerId: string): boolean =>
  p.id !== viewerId && (p.role === 'mandant' || p.sensitiveLabels.some((s) => !s.sharedOutsideTeam));

/**
 * Projection « famille » : le mandant et les personnes sensibles sont anonymisés pour les autres héritiers (jamais nommé), seules les coordonnées
 * partagées avec consentement subsistent, ni mentions sensibles, ni statut de relance, ni parts (estimation réservée
 * à l'équipe). Aucune donnée retirée ici n'atteint la page, même cachée dans le balisage.
 */
export function vueFamille(d: DonneesArbre, viewerId: string): DonneesArbre {
  return {
    personnes: d.personnes.map((p) => {
      const anonyme = estAnonymeEnFamille(p, viewerId);
      return {
        ...p,
        ...(anonyme ? { firstNames: '', lastName: '', birthName: null } : {}),
        contacts: anonyme ? [] : p.contacts.filter(partage),
        sensitiveLabels: [],
        reachability: 'joignable' as const,
      };
    }),
    relations: d.relations,
    successions: d.successions,
    devolution: { ...d.devolution, shares: [], reserves: [], explanation: [], spouseOption: null },
    ...(d.residences ? { residences: d.residences } : {}),
  };
}

/** X15 « Mon arbre » : le même arbre que celui de l'équipe, sans tableau des vivants et sans coordonnées non partagées. */
export function ArbreClient({
  donnees,
  viewerId,
  statutsPortail,
}: {
  donnees: DonneesArbre;
  viewerId: string;
  /** Portail réel (ARB-GENEA-46) : statuts portés par l'API (« accepte » seulement) ; sinon calculés sur les données de l'équipe. */
  statutsPortail?: Record<string, StatutContact>;
}) {
  const statuts = statutsPortail ? { ...statutsPortail } : statutsContact(donnees);
  for (const p of donnees.personnes) if (estAnonymeEnFamille(p, viewerId)) delete statuts[p.id];
  return (
    <section data-vue="famille">
      <ArbreInteractif
        donnees={vueFamille(donnees, viewerId)}
        disposition="classique"
        vue="famille"
        afficher={{ quotesParts: false, defuntsGrises: true }}
        statuts={statuts}
        viewerId={viewerId}
        onOuvrir={() => undefined}
      />
    </section>
  );
}
